import { compact } from 'lodash'
import {
  Archive,
  ArrowLeft,
  ArrowUp,
  Check,
  EditPencil,
  InfoCircle,
  MailIn,
  MoreVert,
  NavArrowRight,
  Refresh,
  Xmark,
} from 'iconoir-react'
import { useEffect, useRef, useState } from 'react'
import { Dropdown } from '../shared/styled/Dropdown'
import { Button } from 'app/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import PhoneNumber from 'app/components/PhoneNumber'
import { UPDATE_THREAD_MUTATION } from 'app/components/inbox/graphql'
import type { ActivityContact } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { useMutation } from 'urql'

export function InboxHeader({
  threadId,
  cellPhone,
  contactName,
  threadName,
  unread,
  incomingCount,
  relatedContacts,
  detailsOpen,
  onToggleDetails,
  onMarkAllRead,
  onMarkUnread,
  onArchive,
  isArchived,
  onBack,
}: {
  threadId?: string
  cellPhone?: string
  contactName?: string
  threadName?: string | null
  unread?: number
  incomingCount?: number
  relatedContacts?: (ActivityContact | ContactJson)[]
  detailsOpen?: boolean
  onToggleDetails?: () => void
  onMarkAllRead?: () => void | Promise<void>
  onMarkUnread?: () => void | Promise<void>
  /**
   * Archive or unarchive the open thread. Optional because the contact drawer
   * mounts this surface without an inbox controller behind it, and the button
   * simply does not render there.
   */
  onArchive?: () => void | Promise<void>
  isArchived?: boolean
  // Mobile master-detail: back arrow that returns to the conversation list.
  onBack?: () => void
}) {
  const customName = threadName ?? undefined

  const names = compact(relatedContacts?.map((c) => c.name))
  const derivedName = names.length === 1 ? names[0] : contactName
  const showDerivedName = derivedName && derivedName.toLowerCase() !== `unknown`

  const displayName = customName ?? (showDerivedName ? derivedName : undefined)
  const hasUnread = (unread ?? 0) > 0

  return (
    <div className="flex h-app-header shrink-0 items-center gap-x-3 px-4 md:px-6">
      {onBack && (
        <button
          type="button"
          aria-label="Back to conversations"
          onClick={onBack}
          className="-ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-subtle transition-colors hover:bg-surface-muted hover:text-strong md:hidden"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </button>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-x-2">
        {displayName ? (
          <span className="min-w-0 truncate text-heading-sm font-semibold text-strong">{displayName}</span>
        ) : (
          <PhoneNumber className="min-w-0 truncate text-heading-sm font-semibold text-strong" number={cellPhone} />
        )}
        {threadId && (
          <RenameThreadPopover
            threadId={threadId}
            currentName={customName ?? derivedName}
            relatedContacts={relatedContacts}
            isCustom={!!customName}
          />
        )}
      </div>
      <div className="hidden items-center gap-x-3 md:flex">
        {hasUnread && onMarkAllRead && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onMarkAllRead()}
            className="rounded-full border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-700"
          >
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            Mark read
          </Button>
        )}
        {!hasUnread && onMarkUnread && <MarkAsUnreadButton incomingCount={incomingCount} onMarkUnread={onMarkUnread} />}
        {onArchive && (
          <TooltipWrapper
            placement="bottom"
            message={isArchived ? `Move back to the inbox` : `Archive this conversation`}
          >
            <Button
              type="button"
              variant="white"
              size="sm"
              shape="pill"
              aria-label={isArchived ? `Unarchive conversation` : `Archive conversation`}
              onClick={() => onArchive()}
            >
              {isArchived ? (
                <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
              ) : (
                <Archive className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              {isArchived ? `Unarchive` : `Archive`}
            </Button>
          </TooltipWrapper>
        )}
      </div>
      {(onMarkAllRead || onMarkUnread || onArchive) && (
        <div className="md:hidden">
          <MobileActionsMenu
            hasUnread={hasUnread}
            incomingCount={incomingCount}
            isArchived={isArchived}
            onMarkAllRead={onMarkAllRead}
            onMarkUnread={onMarkUnread}
            onArchive={onArchive}
          />
        </div>
      )}
      {onToggleDetails && (
        <TooltipWrapper placement="bottom-end" message={detailsOpen ? `Hide details` : `About this number`}>
          <Button
            type="button"
            variant="subtle"
            size="icon"
            aria-label={detailsOpen ? `Hide details` : `Show details`}
            onClick={onToggleDetails}
          >
            {detailsOpen ? <NavArrowRight className="h-5 w-5" /> : <InfoCircle className="h-5 w-5" strokeWidth={2} />}
          </Button>
        </TooltipWrapper>
      )}
    </div>
  )
}

function MobileActionsMenu({
  hasUnread,
  incomingCount,
  isArchived,
  onMarkAllRead,
  onMarkUnread,
  onArchive,
}: {
  hasUnread: boolean
  incomingCount?: number
  isArchived?: boolean
  onMarkAllRead?: () => void | Promise<void>
  onMarkUnread?: () => void | Promise<void>
  onArchive?: () => void | Promise<void>
}) {
  const canMarkUnread = (incomingCount ?? 0) > 0
  const itemClass = `flex w-full items-center gap-x-2 px-3 py-2 text-left text-label-xs text-default hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-50`
  return (
    <Dropdown
      placement="bottom-end"
      closeOnClick
      button={
        <Button type="button" variant="subtle" size="icon" aria-label="Conversation actions">
          <MoreVert className="h-5 w-5" strokeWidth={2} />
        </Button>
      }
    >
      <div className="min-w-44 py-1">
        {hasUnread && onMarkAllRead && (
          <button type="button" onClick={() => onMarkAllRead()} className={itemClass}>
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            Mark read
          </button>
        )}
        {!hasUnread && onMarkUnread && (
          <button type="button" disabled={!canMarkUnread} onClick={() => onMarkUnread()} className={itemClass}>
            <MailIn className="h-3.5 w-3.5" strokeWidth={2} />
            Mark as unread
          </button>
        )}
        {onArchive && (
          <button type="button" onClick={() => onArchive()} className={itemClass}>
            {isArchived ? (
              <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
            ) : (
              <Archive className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            {isArchived ? `Unarchive` : `Archive`}
          </button>
        )}
      </div>
    </Dropdown>
  )
}

/**
 * Mark-as-unread is its own component so the canMark/tooltip computation only
 * runs when the button actually renders, not on every parent render. Keeps the
 * conditional path light and the wrapping IIFE out of the parent's JSX.
 */
function MarkAsUnreadButton({
  incomingCount,
  onMarkUnread,
}: {
  incomingCount?: number
  onMarkUnread: () => void | Promise<void>
}) {
  const canMark = (incomingCount ?? 0) > 0
  return (
    <TooltipWrapper
      placement="bottom"
      pinOnClick={!canMark}
      message={canMark ? `` : `Nothing to mark unread. No customer messages in this thread`}
    >
      <Button
        type="button"
        variant="white"
        size="sm"
        shape="pill"
        aria-label="Mark as unread"
        disabled={!canMark}
        onClick={() => onMarkUnread()}
      >
        <MailIn className="h-3.5 w-3.5" strokeWidth={2} />
        Mark as unread
      </Button>
    </TooltipWrapper>
  )
}

function RenameThreadPopover({
  threadId,
  currentName,
  relatedContacts,
  isCustom,
}: {
  threadId: string
  currentName?: string
  relatedContacts?: (ActivityContact | ContactJson)[]
  isCustom: boolean
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(currentName ?? ``)
  const inputRef = useRef<HTMLInputElement>(null)
  const [, updateThread] = useMutation(UPDATE_THREAD_MUTATION)
  const suggestions = compact(relatedContacts?.map((c) => c.name)).filter((n) => n.toLowerCase() !== `unknown`)

  useEffect(() => {
    if (open) {
      setDraft(currentName ?? ``)
      setTimeout(() => inputRef.current?.select(), 0)
    }
  }, [open, currentName])

  const save = (next: string | undefined) => {
    updateThread({ id: threadId, input: { name: next ?? null } })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipWrapper placement="bottom" message={isCustom ? `Edit thread name` : `Name this thread`}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={isCustom ? `Edit thread name` : `Name this thread`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-minimal transition-colors hover:bg-surface-muted hover:text-default"
          >
            <EditPencil className="h-4 w-4" strokeWidth={2} />
          </button>
        </PopoverTrigger>
      </TooltipWrapper>
      <PopoverContent align="start" sideOffset={6} className="w-80 p-3 shadow-elevation-2">
        <p className="mb-2 text-label-xs font-semibold uppercase tracking-wide text-subtle">Thread name</p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save(draft)
          }}
          className="flex items-center gap-x-2"
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Custom name"
            className="h-9 min-w-0 flex-1 rounded-md border border-default bg-surface-raised px-2 text-body-sm text-strong placeholder:text-minimal focus:border-primary-500 focus:outline-none focus:ring-0"
          />
          <button
            type="submit"
            disabled={!draft.trim() || draft.trim() === (currentName ?? ``)}
            className="flex h-9 items-center rounded-md bg-primary-500 px-3 text-label-xs font-semibold text-on-color transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save
          </button>
        </form>

        {suggestions.length > 0 && (
          <>
            <p className="mb-1 mt-3 text-label-xs font-semibold uppercase tracking-wide text-subtle">
              Use a related contact
            </p>
            <div className="flex flex-col">
              {suggestions.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => save(name)}
                  className="flex items-center justify-between rounded-md px-2 py-1.5 text-left text-body-sm text-default hover:bg-surface-subtle"
                >
                  <span className="min-w-0 truncate">{name}</span>
                  {name === currentName && <Check className="h-4 w-4 text-primary-500" strokeWidth={2} />}
                </button>
              ))}
            </div>
          </>
        )}

        {isCustom && (
          <button
            type="button"
            onClick={() => save(undefined)}
            className="mt-3 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-label-xs font-medium text-subtle transition-colors hover:bg-surface-muted hover:text-default"
          >
            <Refresh className="h-3.5 w-3.5" strokeWidth={2} />
            Reset to default
          </button>
        )}
        {!isCustom && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-label-xs font-medium text-subtle transition-colors hover:bg-surface-muted hover:text-default"
          >
            <Xmark className="h-3.5 w-3.5" strokeWidth={2} />
            Cancel
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
